import { z } from "zod";
import type { CadPreviewResult, CadProvider, HouseConfiguration } from "./types";

const cadPreviewSchema = z.object({
  requestId: z.string(),
  jobId: z.string(),
  status: z.literal("completed"),
  configurationHash: z.string(),
  assets: z.object({
    model3dUrl: z.string(),
    floorPlanUrl: z.string()
  }),
  metrics: z.object({
    grossAreaM2: z.number(),
    bedrooms: z.number(),
    bathrooms: z.number()
  }),
  warnings: z.array(z.string())
});

const wait = (duration: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, duration);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Request aborted", "AbortError"));
      },
      { once: true }
    );
  });

export class MockCadProvider implements CadProvider {
  async requestPreview(
    configuration: HouseConfiguration,
    signal?: AbortSignal
  ): Promise<CadPreviewResult> {
    const requestId = crypto.randomUUID();
    await wait(700 + Math.random() * 450, signal);

    return cadPreviewSchema.parse({
      requestId,
      jobId: `mock-${requestId.slice(0, 8)}`,
      status: "completed",
      configurationHash: `${configuration.modelId}-${configuration.areaM2}-${configuration.layoutId}`,
      assets: {
        model3dUrl: `/models/cbciso-${configuration.layoutId}-${configuration.areaM2}.glb?v=3`,
        floorPlanUrl: `/floorplans/${configuration.layoutId}.svg?v=3`
      },
      metrics: {
        grossAreaM2: configuration.areaM2,
        bedrooms: configuration.bedrooms,
        bathrooms: configuration.bathrooms
      },
      warnings: []
    });
  }
}

export class ExternalCadProvider implements CadProvider {
  constructor(private readonly endpoint = "/api/cad/preview") {}

  async requestPreview(
    configuration: HouseConfiguration,
    signal?: AbortSignal
  ): Promise<CadPreviewResult> {
    const response = await fetch(this.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requestId: crypto.randomUUID(),
        configuration
      }),
      signal
    });

    if (!response.ok) throw new Error("CAD preview kunne ikke genereres");
    return cadPreviewSchema.parse(await response.json());
  }
}

export const cadProvider: CadProvider = new MockCadProvider();
